import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import {
  DimeSidebarInset,
  DimeSidebarProvider,
  DimeToaster,
  readDimeSidebarDefaultOpen,
} from '@nudool/design-system';
import * as Sentry from '@sentry/react';
import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { AppHeader } from '../components/AppHeader.js';
import { AppSidebar } from '../components/AppSidebar.js';
import { BreadcrumbLabelsProvider } from '../components/BreadcrumbLabelsContext.js';
import { MissingLookupsNotice } from '../components/MissingLookupsNotice.js';
import { useDocumentTitle } from '../hooks/use-document-title.js';
import { PUBLIC_PATHNAMES } from '../lib/public-pathnames.js';
import { GET_ME, LOGOUT } from '../modules/iam/operations/index.js';
import { PermissionsProvider } from '../modules/iam/permissions-context.js';
import { useAccessDirectory } from '../modules/iam/use-access-directory.js';

// WHY: useDocumentTitle needs useBreadcrumbLabels(), which only resolves inside BreadcrumbLabelsProvider.
// eslint-disable-next-line react-refresh/only-export-components
function DocumentTitleEffect() {
  useDocumentTitle();
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
function RootComponent() {
  const isPublicPage = useRouterState({
    select: (state) => PUBLIC_PATHNAMES.has(state.location.pathname),
  });
  // WHY: skipped on public pages, since an active GET_ME gets refetched by oauth-consent's identifyViaGoogle mutation (via apollo/client.ts's refetchQueries: 'active'), and that refetch's unauthorized result would fail identifyViaGoogle's own awaitRefetchQueries promise.
  const { data, loading } = useQuery(GET_ME, { skip: isPublicPage });
  const accessDirectory = useAccessDirectory(isPublicPage ? null : data?.me?.id);
  const navigate = useNavigate();
  const client = useApolloClient();
  const [logout] = useMutation(LOGOUT);
  const [sidebarDefaultOpen] = useState(readDimeSidebarDefaultOpen);

  const handleLogout = async () => {
    await logout();
    await client.clearStore();
    void navigate({ to: '/login' });
  };

  useEffect(() => {
    if (loading) return;
    if (!data?.me && !isPublicPage) void navigate({ to: '/login' });
  }, [loading, data, isPublicPage, navigate]);

  useEffect(() => {
    if (loading) return;
    Sentry.setUser(
      data?.me ? { id: data.me.id ?? undefined, email: data.me.email ?? undefined } : null,
    );
  }, [loading, data]);

  return (
    // Wraps both branches below (not just the authenticated one) so useDocumentTitle keeps the
    // tab title in sync on public pages like /login too, via one shared provider instance --
    // also read by AppHeader (via AppBreadcrumbs) and written to by a detail page's useBreadcrumbLabel.
    <BreadcrumbLabelsProvider>
      <DocumentTitleEffect />
      {isPublicPage ? (
        <>
          <Outlet />
          <DimeToaster richColors />
        </>
      ) : (
        <PermissionsProvider
          permissions={data?.me?.permissions ?? []}
          hasActiveBrandMembership={data?.me?.hasActiveBrandMembership ?? false}
          memberBrands={data?.me?.brands}
          readableBrands={data?.me?.readableBrands}
          hasMultiStateAccess={data?.me?.hasMultiStateAccess ?? false}
          roleNames={accessDirectory?.roleNames}
          grantingRoles={accessDirectory?.grantingRoles}
          accessAdmins={accessDirectory?.accessAdmins}
          // WHY: gates on `!data`, not just `loading`, so a mutation's background refetchQueries: 'active' refetch of GET_ME (apollo/client.ts) can't re-arm this as loading and unmount/remount Outlet, discarding an unrelated in-progress form.
          loading={loading && !data}
        >
          <DimeSidebarProvider defaultOpen={sidebarDefaultOpen} className="h-svh">
            <AppSidebar onLogout={() => void handleLogout()} />
            {/* AppHeader must stay outside <main> (DimeSidebarInset) -- a <header> only keeps
                its implicit ARIA "banner" landmark role when it isn't nested inside main/
                article/aside/nav/section. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <AppHeader
                firstName={data?.me?.firstName}
                lastName={data?.me?.lastName}
                email={data?.me?.email}
              />
              <DimeSidebarInset className="min-h-0 overflow-hidden">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto p-4">
                  <MissingLookupsNotice />
                  <Outlet />
                </div>
              </DimeSidebarInset>
            </div>
          </DimeSidebarProvider>
          <DimeToaster richColors />
        </PermissionsProvider>
      )}
    </BreadcrumbLabelsProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
